import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Download, UserPlus, Award, BarChart3, Sparkles, ArrowRight, CheckCircle, DollarSign, BookOpen, Upload, Trophy, CreditCard, Zap, TrendingUp, Users, Target, Medal, Menu, X, HelpCircle, Info } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
} from 'recharts';
import DateRangePicker from './DateRangePicker';
import SummaryCards from './SummaryCards';
import Button from './ui/Button';
import { formatCurrency } from '../utils/helpers';
import { Link } from 'react-router-dom';

// Generate dynamic date range (last 7 days)
const generateDynamicDateRange = () => {
  const dates = [];
  const today = new Date();
  
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    dates.push(date);
  }
  
  return dates;
};

// Format date to YYYY-MM-DD
const formatDateYYYYMMDD = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Format date to "Mon DD"
const formatDateLabel = (date) => {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Generate dynamic mock data based on current date
const generateMockData = () => {
  const dates = generateDynamicDateRange();
  const baseMetrics = [
    { spend: 4850, revenue: 12275, commission: 388, purchases: 67, adCount: 12, roas: 2.53 },
    { spend: 5120, revenue: 13568, commission: 410, purchases: 74, adCount: 14, roas: 2.65 },
    { spend: 3450, revenue: 8970, commission: 276, purchases: 49, adCount: 9, roas: 2.60 },
    { spend: 4220, revenue: 10972, commission: 338, purchases: 58, adCount: 11, roas: 2.60 },
    { spend: 5680, revenue: 15048, commission: 454, purchases: 82, adCount: 15, roas: 2.65 },
    { spend: 6150, revenue: 16605, commission: 492, purchases: 91, adCount: 17, roas: 2.70 },
    { spend: 5892, revenue: 15877, commission: 471, purchases: 87, adCount: 16, roas: 2.69 }
  ];

  return dates.map((date, index) => ({
    date: formatDateLabel(date),
    dateISO: formatDateYYYYMMDD(date),
    ...baseMetrics[index]
  }));
};

// Mock leaderboard data
const mockLeaderboardData = [
  { rank: 1, name: 'Alex Martinez', adCount: 94, adSpend: 35362, commission: 2829, roas: 2.64, trend: '+12%' },
  { rank: 2, name: 'Sarah Johnson', adCount: 87, adSpend: 32450, commission: 2596, roas: 2.58, trend: '+8%' },
  { rank: 3, name: 'Mike Chen', adCount: 82, adSpend: 29870, commission: 2389, roas: 2.52, trend: '+15%' },
  { rank: 4, name: 'Emma Davis', adCount: 76, adSpend: 28350, commission: 2268, roas: 2.48, trend: '+5%' },
  { rank: 5, name: 'Chris Wilson', adCount: 71, adSpend: 26780, commission: 2142, roas: 2.45, trend: '+10%' },
  { rank: 6, name: 'James Taylor', adCount: 68, adSpend: 25100, commission: 2008, roas: 2.40, trend: '+7%' },
  { rank: 7, name: 'Lisa Anderson', adCount: 65, adSpend: 23890, commission: 1911, roas: 2.38, trend: '+9%' },
  { rank: 8, name: 'David Kim', adCount: 62, adSpend: 22450, commission: 1796, roas: 2.35, trend: '+6%' },
  { rank: 9, name: 'Maria Garcia', adCount: 58, adSpend: 21200, commission: 1696, roas: 2.32, trend: '+11%' },
  { rank: 10, name: 'Ryan White', adCount: 55, adSpend: 19980, commission: 1598, roas: 2.28, trend: '+4%' },
];

// Get rank icon based on position
const getRankIcon = (rank) => {
  if (rank === 1) return <Medal className="h-6 w-6 text-yellow-400" />;
  if (rank === 2) return <Medal className="h-6 w-6 text-gray-300" />;
  if (rank === 3) return <Medal className="h-6 w-6 text-orange-400" />;
  return <span className="text-gray-400 font-bold">#{rank}</span>;
};

const SidebarLink = ({ icon: Icon, children, active = false, onClick, collapsed }) => {
  return (
    <button
      onClick={onClick}
      className={`group flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-300 relative overflow-hidden w-full ${collapsed ? 'justify-center' : 'text-left'} ${
        active
          ? "bg-gradient-to-r from-[#B8460E]/20 to-[#8B3A0E]/20 text-[#B8460E] shadow-lg shadow-[#B8460E]/10 border border-[#B8460E]/30"
          : "text-gray-300 hover:text-white hover:bg-white/10 hover:shadow-md"
      }`}
      title={collapsed ? children : ''}
    >
      {active && (
        <div className="absolute inset-0 bg-gradient-to-r from-[#B8460E]/10 to-transparent animate-pulse"></div>
      )}
      <Icon
        className={`h-5 w-5 relative z-10 transition-all duration-300 ${collapsed ? '' : 'flex-shrink-0'} ${
          active ? "text-[#B8460E]" : "text-gray-400 group-hover:text-white"
        }`}
      />
      {!collapsed && <span className="relative z-10 transition-all duration-300">{children}</span>}
    </button>
  );
};


// Tooltip using Portal to render at root level (never gets buried)
const InfoTooltip = ({ title, description, position = 'right' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const tooltipWidth = 288; // w-72
      const tooltipHeight = 140;
      
      let top = 0;
      let left = 0;
      
      // Simple positioning: try to the right, fallback to left
      if (position === 'bottom' || position === 'right') {
        left = rect.right + 12;
        top = rect.top;
        
        // If doesn't fit on right, put on left
        if (left + tooltipWidth > window.innerWidth - 20) {
          left = rect.left - tooltipWidth - 12;
        }
      } else if (position === 'left') {
        left = rect.left - tooltipWidth - 12;
        top = rect.top;
        
        // If doesn't fit on left, put on right
        if (left < 20) {
          left = rect.right + 12;
        }
      } else if (position === 'top') {
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        top = rect.top - tooltipHeight - 12;
        
        // If doesn't fit above, put below
        if (top < 20) {
          top = rect.bottom + 12;
        }
      }
      
      // Keep within bounds
      left = Math.max(20, Math.min(left, window.innerWidth - tooltipWidth - 20));
      top = Math.max(20, Math.min(top, window.innerHeight - tooltipHeight - 20));
      
      setTooltipPos({ top, left });
    }
  }, [isOpen, position]);

  const tooltipContent = isOpen && (
    <div 
      className="fixed z-[999999] w-72 pointer-events-none animate-in fade-in duration-200"
      style={{
        top: `${tooltipPos.top}px`,
        left: `${tooltipPos.left}px`,
      }}
    >
      <div className="bg-black/95 backdrop-blur-xl border-2 border-orange-500 rounded-lg shadow-2xl p-3">
        <div className="relative">
          <h4 className="text-xs font-bold text-white mb-1 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-[#B8460E]" />
            {title}
          </h4>
          <p className="text-[11px] text-gray-300 leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="group inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#B8460E]/20 hover:bg-[#B8460E]/40 border border-[#B8460E]/30 hover:border-[#B8460E]/60 transition-all duration-200 hover:scale-110 flex-shrink-0"
      >
        <Info className="h-2.5 w-2.5 text-[#B8460E] group-hover:text-white transition-colors" />
      </button>

      {tooltipContent && ReactDOM.createPortal(tooltipContent, document.body)}
    </>
  );
};

const LandingPage = () => {
  // Generate dynamic mock data
  const mockDailyPerformance = useMemo(() => generateMockData(), []);
  
  // Calculate dynamic date range
  const dynamicDateRange = useMemo(() => {
    const dates = generateDynamicDateRange();
    return {
      start: formatDateYYYYMMDD(dates[0]),
      end: formatDateYYYYMMDD(dates[dates.length - 1]),
      availableDates: dates.map(formatDateYYYYMMDD)
    };
  }, []);

  // State for dashboard demo filters (non-functional)
  const [dateFilterType, setDateFilterType] = useState('range');
  const [singleDate, setSingleDate] = useState('');
  const [dateRangeStart, setDateRangeStart] = useState(dynamicDateRange.start);
  const [dateRangeEnd, setDateRangeEnd] = useState(dynamicDateRange.end);
  const [brand, setBrand] = useState('all');
  const [chartMetric, setChartMetric] = useState('commission');
  const [chartGroupBy, setChartGroupBy] = useState('daily');
  const [activeView, setActiveView] = useState('performance'); // 'performance' or 'leaderboard' or 'payout' etc
  const [showFeaturePreview, setShowFeaturePreview] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(true);

  // Close welcome modal
  const handleStartExperience = () => {
    setShowWelcomeModal(false);
  };


  // Calculate summary stats from mock data
  const summaryStats = useMemo(() => {
    const totalAdSpend = mockDailyPerformance.reduce((sum, day) => sum + day.spend, 0);
    const totalRevenue = mockDailyPerformance.reduce((sum, day) => sum + day.revenue, 0);
    const totalCommission = mockDailyPerformance.reduce((sum, day) => sum + day.commission, 0);
    const avgROAS = totalAdSpend > 0 ? totalRevenue / totalAdSpend : 0;

    return {
      totalAdSpend,
      totalRevenue,
      totalCommission,
      avgROAS,
    };
  }, [mockDailyPerformance]);

  // Process chart data
  const processedChartData = useMemo(() => {
    return mockDailyPerformance.map(day => ({
      ...day,
      label: day.date,
      fullLabel: day.date,
      value: day[chartMetric] || 0,
      period: day.date,
    }));
  }, [mockDailyPerformance, chartMetric]);

  const exportResults = () => {
    alert('Join Olympus to unlock export functionality!');
  };

  const handleFeatureClick = (feature) => {
    if (feature === 'leaderboard') {
      setActiveView('leaderboard');
      setShowFeaturePreview(null);
    } else {
      setShowFeaturePreview(feature);
    }
  };

  const featurePreviews = {
    payout: {
      title: 'Payouts',
      description: 'View your earnings, payment history, and schedule. Track all commission payouts in one place.'
    },
    creatives: {
      title: 'Submit Creatives',
      description: 'Upload your ad creatives for approval. Fast review process with real-time status updates.'
    },
    training: {
      title: 'Training',
      description: 'Access comprehensive training materials, video courses, and proven strategies from top performers.'
    },
    products: {
      title: 'Products',
      description: 'Browse all available products to promote. Detailed product information and marketing assets included.'
    },
    referrals: {
      title: 'Referrals',
      description: 'Invite other affiliates and earn bonus commissions. Build your team and increase your earnings.'
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white overflow-x-hidden">
      {/* Welcome Modal */}
      {showWelcomeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-2xl transition-opacity duration-500">
          <div className="bg-white/10 backdrop-blur-3xl rounded-3xl border border-white/20 shadow-2xl max-w-3xl w-full p-6 md:p-8 relative overflow-hidden transform transition-all duration-500 scale-100">
            {/* Animated background effects */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute w-96 h-96 bg-[#B8460E]/20 rounded-full blur-3xl animate-pulse top-0 right-0 -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse bottom-0 left-0 translate-y-1/2 -translate-x-1/2"></div>
            </div>

            <div className="relative z-10 text-center font-helvetica">
              {/* Logo */}
              <div className="flex justify-center mb-4">
                <div className="w-28 h-14">
                  <img
                    src="/whiteolympus.png"
                    alt="White Olympus Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-[#B8460E]/20 backdrop-blur-xl border border-[#B8460E]/30 text-[#B8460E] px-3 py-1.5 rounded-full mb-4">
                <Sparkles className="h-3.5 w-3.5" />
                <span className="font-bold text-xs tracking-wide">LIVE DASHBOARD PREVIEW</span>
              </div>

              {/* Headline */}
              <h1 className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight tracking-tight">
                Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B8460E] to-orange-500">Olympus</span>
              </h1>

              {/* Description */}
              <div className="mb-5 space-y-2">
                <p className="text-lg md:text-xl leading-relaxed font-light">
                  The <span className="font-black text-white italic">first</span> affiliate marketing platform in the world
                </p>
                
                <p className="text-xl md:text-2xl font-bold leading-tight tracking-tight">
                  that <span className="font-black text-transparent bg-clip-text bg-gradient-to-r from-[#B8460E] to-orange-500">funds your ads</span> for you.
                </p>
                
                <div className="pt-2">
                  <p className="text-xs text-gray-400 font-mono max-w-xl mx-auto">
                    // Demo preview: Track performance, manage payouts, scale—while we fund <span className="text-[#B8460E] font-bold">100%</span> of your ad spend
                  </p>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleStartExperience}
                className="group relative bg-gradient-to-r from-[#B8460E] to-orange-600 text-white px-6 py-3 rounded-xl font-bold text-base shadow-2xl hover:shadow-[#B8460E]/50 transition-all duration-300 hover:scale-105 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-[#FF6B35] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative flex items-center gap-2 justify-center">
                  <span>Let's See It</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Main Content - Blur when modal is open */}
      <div className={`transition-all duration-500 ${showWelcomeModal ? 'blur-xl scale-105' : 'blur-0 scale-100'}`}>
        
        {/* Compact Top Header */}
        <header className="bg-black/60 backdrop-blur-2xl border-b border-white/10 sticky top-0 z-40 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14">
              {/* Left: Logo */}
              <div className="flex items-center">
                <div className="w-24 h-10">
                  <img
                    src="/whiteolympus.png"
                    alt="Olympus Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              
              {/* Right: Buttons */}
              <div className="flex items-center gap-2">
                <div className="hidden md:flex items-center gap-2 bg-[#B8460E]/20 backdrop-blur-xl border border-[#B8460E]/30 text-[#B8460E] px-3 py-1.5 rounded-full">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span className="font-bold text-xs">LIVE DEMO</span>
                </div>
                <Button
                  onClick={() => window.location.href = '/register'}
                  className="bg-gradient-to-r from-[#B8460E] to-orange-600 hover:from-[#8B3A0E] hover:to-[#B8460E] text-white font-semibold rounded-xl px-5 py-1.5 text-sm shadow-xl"
                >
                  Join Free
                </Button>
              </div>
            </div>
          </div>
        </header>

      {/* Sidebar - Fixed, collapsible */}
      <aside 
        className={`hidden md:block fixed top-14 left-0 shadow-2xl z-30 transition-all duration-300 ${
          sidebarCollapsed ? 'w-20' : 'w-72'
        }`}
        style={{ 
          height: 'calc(100vh - 3.5rem)',
          backdropFilter: sidebarCollapsed ? 'blur(24px)' : 'blur(80px) saturate(150%)',
          WebkitBackdropFilter: sidebarCollapsed ? 'blur(24px)' : 'blur(80px) saturate(150%)',
        }}
      >
        <div className={`absolute inset-0 transition-all duration-300 ${
          sidebarCollapsed ? 'bg-black/40' : 'bg-black/75'
        }`}></div>
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-white/5 pointer-events-none"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#B8460E]/5 via-transparent to-transparent pointer-events-none"></div>
        
        {/* Bottom fade to black */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black via-black/80 to-transparent pointer-events-none"></div>

        <div className="relative z-10 h-full flex flex-col">
            {/* Toggle Button */}
            <div className={`px-4 py-4 flex ${sidebarCollapsed ? 'justify-center' : 'justify-end'}`}>
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-all"
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed ? <Menu className="h-5 w-5" /> : <X className="h-5 w-5" />}
              </button>
            </div>

            <nav className={`flex-1 ${sidebarCollapsed ? 'px-2' : 'px-6'} py-2 space-y-2 overflow-y-auto`}>
              <div className="mb-6">
                {!sidebarCollapsed && (
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 mb-2">
                    Performance
                  </p>
                )}
                <SidebarLink 
                  icon={BarChart3} 
                  active={activeView === 'performance'}
                  onClick={() => {
                    setActiveView('performance');
                    setShowFeaturePreview(null);
                  }}
                  collapsed={sidebarCollapsed}
                >
                  Dashboard
                </SidebarLink>
                <div id="leaderboard-trigger">
                  <SidebarLink 
                    icon={Trophy} 
                    active={activeView === 'leaderboard'}
                    onClick={() => handleFeatureClick('leaderboard')}
                    collapsed={sidebarCollapsed}
                  >
                    Leaderboard
                  </SidebarLink>
                </div>
                <SidebarLink 
                  icon={DollarSign}
                  onClick={() => handleFeatureClick('payout')}
                  collapsed={sidebarCollapsed}
                >
                  Payouts
                </SidebarLink>
              </div>

              <div className="mb-6">
                {!sidebarCollapsed && (
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 mb-2">
                    Resources
                  </p>
                )}
                <SidebarLink 
                  icon={Upload}
                  onClick={() => handleFeatureClick('creatives')}
                  collapsed={sidebarCollapsed}
                >
                  Submit Creatives
                </SidebarLink>
                <SidebarLink 
                  icon={BookOpen}
                  onClick={() => handleFeatureClick('training')}
                  collapsed={sidebarCollapsed}
                >
                  Training
                </SidebarLink>
                <SidebarLink 
                  icon={Target}
                  onClick={() => handleFeatureClick('products')}
                  collapsed={sidebarCollapsed}
                >
                  Products
                </SidebarLink>
              </div>

              <div>
                {!sidebarCollapsed && (
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 mb-2">
                    Account
                  </p>
                )}
                <SidebarLink 
                  icon={Users}
                  onClick={() => handleFeatureClick('referrals')}
                  collapsed={sidebarCollapsed}
                >
                  Referrals
                </SidebarLink>
              </div>
            </nav>
          </div>
      </aside>

      {/* Dashboard Section */}
      <div className="w-full">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4">
            {activeView === 'performance' ? (
              // Performance Dashboard View
              <div className="space-y-4">
                {/* Top Control Row - Affiliate View with Summary Cards */}
                <div id="dashboard-header" className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
                  <div className="flex flex-col items-start gap-3 w-full lg:w-auto">
                    <DateRangePicker
                      dateFilterType={dateFilterType}
                      setDateFilterType={setDateFilterType}
                      singleDate={singleDate}
                      setSingleDate={setSingleDate}
                      dateRangeStart={dateRangeStart}
                      setDateRangeStart={setDateRangeStart}
                      dateRangeEnd={dateRangeEnd}
                      setDateRangeEnd={setDateRangeEnd}
                      availableDates={dynamicDateRange.availableDates}
                    />
                  </div>

                  {/* Summary Cards - Compact in Top Right */}
                  <div id="summary-cards" className="grid grid-cols-2 lg:flex gap-3 w-full lg:w-auto">
                    <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-3 shadow-xl relative overflow-hidden group lg:min-w-[200px]">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent"></div>
                      <div className="relative z-10">
                        <p className="text-gray-400 text-xs font-medium mb-2 flex items-center gap-1.5">
                          <span className="truncate">Total Ad Spend</span>
                          <InfoTooltip
                            title="We Fund Everything"
                            description="This is the total amount spent on ads. You never pay a cent - we fund 100% of this. Zero risk for you!"
                            position="bottom"
                          />
                        </p>
                        <div className="flex flex-col lg:flex-row lg:items-center gap-1 lg:gap-2">
                          <p className="text-white text-lg lg:text-2xl font-black">{formatCurrency(summaryStats.totalAdSpend)}</p>
                          <div className="bg-blue-500/20 backdrop-blur-sm border border-blue-500/30 text-blue-400 px-2 py-0.5 rounded-md flex items-center gap-1 w-fit">
                            <DollarSign className="h-3 w-3" />
                            <span className="text-xs font-bold">100%</span>
                          </div>
                        </div>
                        <p className="text-gray-500 text-xs mt-1.5">We fund everything</p>
                      </div>
                    </div>
                    <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-3 shadow-xl relative overflow-hidden group lg:min-w-[200px]">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#B8460E]/5 to-transparent"></div>
                      <div className="relative z-10">
                        <p className="text-gray-400 text-xs font-medium mb-2 flex items-center gap-1.5">
                          <span className="truncate">Total Commissions</span>
                          <InfoTooltip
                            title="Your Earnings"
                            description="This is what you've earned! Commission rates range from 5.5% to 8% based on your ROAS performance. The better your ads perform, the more you earn."
                            position="bottom"
                          />
                        </p>
                        <div className="flex flex-col lg:flex-row lg:items-center gap-1 lg:gap-2">
                          <p className="text-white text-lg lg:text-2xl font-black">{formatCurrency(summaryStats.totalCommission)}</p>
                          <div className="bg-green-500/20 backdrop-blur-sm border border-green-500/30 text-green-400 px-2 py-0.5 rounded-md flex items-center gap-1 w-fit">
                            <TrendingUp className="h-3 w-3" />
                            <span className="text-xs font-bold">+12%</span>
                          </div>
                        </div>
                        <p className="text-gray-500 text-xs mt-1.5">Vs last week</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Performance Chart */}
                <div id="performance-chart" className="bg-white/5 backdrop-blur-xl rounded-lg sm:rounded-2xl border border-white/10 p-2 lg:p-4 shadow-xl">
                  <div className="flex flex-col gap-3 mb-2 lg:mb-4">
                    {/* Header with Badge */}
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                      <div>
                        <h3 className="text-lg lg:text-xl font-bold text-white flex items-center gap-2">
                          Performance Analytics
                          <InfoTooltip
                            title="Real-Time Performance"
                            description="This chart shows your daily ad performance. We fund 100% of the ad spend shown here, and you keep all the commission. Switch between metrics to see different views of your success."
                            position="right"
                          />
                        </h3>
                        <p className="text-xs lg:text-sm text-gray-400">
                          Track your ad spend, revenue, and performance over time
                        </p>
                      </div>

                      <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2 lg:gap-3 w-full lg:w-auto">
                        {/* Metric Selector */}
                        <div className="flex items-center gap-2 w-full lg:w-auto">
                          <span className="text-gray-400 text-sm whitespace-nowrap">Metric:</span>
                          <select
                            value={chartMetric}
                            onChange={(e) => setChartMetric(e.target.value)}
                            className="bg-gray-800/50 border border-gray-700 text-white rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 flex-1 lg:flex-none"
                          >
                            <option value="spend">Ad Spend</option>
                            <option value="revenue">Revenue</option>
                            <option value="purchases">Purchases</option>
                            <option value="commission">Commission</option>
                            <option value="roas">Avg ROAS</option>
                          </select>
                        </div>

                        {/* Motivational Badge */}
                        <div className="group relative bg-gradient-to-r from-[#B8460E]/20 to-orange-500/20 backdrop-blur-sm border border-[#B8460E]/40 rounded-full px-4 py-2 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex-shrink-0 w-full lg:w-auto">
                          <div className="absolute inset-0 bg-gradient-to-r from-[#B8460E]/10 to-orange-500/10 rounded-full blur-sm group-hover:blur-md transition-all"></div>
                          <p className="relative text-xs font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-200 flex items-center justify-center lg:justify-start gap-1.5 whitespace-nowrap">
                            <Sparkles className="h-3.5 w-3.5 text-[#B8460E] animate-pulse" />
                            This Could Be You
                            <TrendingUp className="h-3.5 w-3.5 text-green-400" />
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-lg sm:rounded-xl p-0 sm:p-1 lg:p-4 border border-white/10">
                    <div className="h-[20rem] lg:h-[30rem]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={processedChartData}
                          margin={{ top: 40, right: 0, left: 0, bottom: 20 }}
                          barSize={30}
                        >
                          <defs>
                            <linearGradient id="dashboardBarGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.8} />
                              <stop offset="95%" stopColor="#B8460E" stopOpacity={0.8} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                          <XAxis
                            dataKey="label"
                            axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                            tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                            tick={{ fontSize: 12, fill: "white" }}
                            angle={-35}
                            textAnchor="end"
                            height={36}
                          />
                          <YAxis
                            axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                            tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                            tick={{ fontSize: 12, fill: "white" }}
                            tickFormatter={(value) => {
                              if (chartMetric === "roas") return value.toFixed(1);
                              if (chartMetric === "purchases") return Math.round(value);
                              return value > 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${Math.round(value)}`;
                            }}
                          />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-black/80 backdrop-blur-2xl text-white text-sm rounded-xl px-4 py-3 border border-white/5 shadow-2xl">
                                    <p className="font-semibold text-[#FF6B35] mb-2 text-center">{data.fullLabel}</p>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                                      <div className="flex items-center">
                                        <div className="w-2 h-2 bg-green-400/60 rounded-full mr-2"></div>
                                        <span className="text-gray-300">Spend:</span>
                                      </div>
                                      <div className="text-green-300/80 font-medium text-right">
                                        ${data.spend?.toLocaleString() || "0"}
                                      </div>
                                      <div className="flex items-center">
                                        <div className="w-2 h-2 bg-blue-400/60 rounded-full mr-2"></div>
                                        <span className="text-gray-300">Revenue:</span>
                                      </div>
                                      <div className="text-blue-300/80 font-medium text-right">
                                        ${data.revenue?.toLocaleString() || "0"}
                                      </div>
                                      <div className="flex items-center">
                                        <div className="w-2 h-2 bg-orange-400/60 rounded-full mr-2"></div>
                                        <span className="text-gray-300">Commission:</span>
                                      </div>
                                      <div className="text-orange-300/80 font-medium text-right">
                                        ${data.commission?.toLocaleString() || "0"}
                                      </div>
                                      <div className="flex items-center">
                                        <div className="w-2 h-2 bg-purple-400/60 rounded-full mr-2"></div>
                                        <span className="text-gray-300">Avg ROAS:</span>
                                      </div>
                                      <div className="text-purple-300/80 font-medium text-right">
                                        {data.roas?.toFixed(2) || "0.00"}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Legend
                            verticalAlign="top"
                            align="center"
                            height={24}
                            wrapperStyle={{ color: "white" }}
                            formatter={() => (
                              <span style={{ color: "white", fontSize: "12px", fontWeight: "500" }}>
                                {chartMetric === "spend" ? "Ad Spend" :
                                 chartMetric === "revenue" ? "Revenue" :
                                 chartMetric === "purchases" ? "Purchases" :
                                 chartMetric === "commission" ? "Commission" : "Avg ROAS"}
                              </span>
                            )}
                          />
                          <Bar
                            dataKey="value"
                            fill="url(#dashboardBarGradient)"
                            radius={[4, 4, 0, 0]}
                            style={{ filter: "drop-shadow(0 0 8px rgba(255, 107, 53, 0.3))" }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Bottom Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-3">
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                      Avg Daily Spend
                      <InfoTooltip
                        title="Daily Performance"
                        description="Your average daily ad spend. We fund this amount every day, and you earn commission on the results."
                        position="bottom"
                      />
                    </p>
                    <p className="text-green-400 text-xl font-bold">
                      {formatCurrency(summaryStats.totalAdSpend / 7)}
                    </p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-3">
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1.5">Avg Daily Revenue</p>
                    <p className="text-blue-400 text-xl font-bold">
                      {formatCurrency(summaryStats.totalRevenue / 7)}
                    </p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-3">
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1.5">Avg Daily Commission</p>
                    <p className="text-orange-400 text-xl font-bold">
                      {formatCurrency(summaryStats.totalCommission / 7)}
                    </p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-3">
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1.5">Avg ROAS</p>
                    <p className="text-yellow-400 text-xl font-bold">
                      {summaryStats.avgROAS.toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-3">
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1.5">Total Purchases</p>
                    <p className="text-purple-400 text-xl font-bold">
                      {mockDailyPerformance.reduce((sum, day) => sum + day.purchases, 0)}
                    </p>
                  </div>
                </div>

                {/* Ad Performance Table */}
                <div id="ad-performance-section" className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl overflow-hidden">
                  <div className="p-3 lg:p-4 border-b border-white/10">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                      <div>
                        <h3 className="text-lg lg:text-xl font-bold text-white flex items-center gap-2">
                          Ad Performance
                          <InfoTooltip
                            title="Track Every Ad"
                            description="See detailed metrics for each ad campaign. Filter, search, and optimize based on real performance data. Every ad you run is tracked here automatically."
                            position="right"
                          />
                        </h3>
                        <p className="text-xs lg:text-sm text-gray-400">105 unique ads (3662 total instances) ✅</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="relative flex-1 min-w-[200px]">
                          <input
                            type="text"
                            placeholder="Search ads..."
                            className="w-full bg-gray-800/50 border border-gray-700 text-white rounded-lg px-4 py-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-white/5 border-b border-white/10">
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-300">Ad Name</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-300">Ad Spend</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-300">Revenue</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-300">ROAS</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-300">Purchases</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-300">Commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="px-4 py-2.5 text-white text-sm font-medium">Zane.Hercules.Honey - 11</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">$808.27</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">$873.73</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">1.08</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">19</td>
                          <td className="px-4 py-2.5 text-right text-[#B8460E] text-sm font-bold">$30.71</td>
                        </tr>
                        <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="px-4 py-2.5 text-white text-sm font-medium">Zane.Hercules.Honey - 12</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">$919.09</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">$1,132.29</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">1.23</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">24</td>
                          <td className="px-4 py-2.5 text-right text-[#B8460E] text-sm font-bold">$41.36</td>
                        </tr>
                        <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="px-4 py-2.5 text-white text-sm font-medium">Zane.Hercules.Honey - 7</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">$646.31</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">$516.94</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">0.80</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">11</td>
                          <td className="px-4 py-2.5 text-right text-gray-400 text-sm font-bold">$0.00</td>
                        </tr>
                        <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="px-4 py-2.5 text-white text-sm font-medium">Zane.Hercules.Gummies - 5</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">$1,536.54</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">$1,493.99</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">0.97</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">29</td>
                          <td className="px-4 py-2.5 text-right text-[#B8460E] text-sm font-bold">$58.39</td>
                        </tr>
                        <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="px-4 py-2.5 text-white text-sm font-medium">Zane.Hercules.Honey - 13</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">$607.88</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">$741.77</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">1.22</td>
                          <td className="px-4 py-2.5 text-right text-white text-sm">15</td>
                          <td className="px-4 py-2.5 text-right text-[#B8460E] text-sm font-bold">$27.35</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="p-3 border-t border-white/10 flex items-center justify-between">
                    <p className="text-sm text-gray-400">Showing 1-5 of 105</p>
                    <div className="flex items-center gap-2">
                      <button className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <span className="text-sm text-white">Page 1 of 21</span>
                      <button className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : activeView === 'leaderboard' ? (
              // Leaderboard View
              <div id="leaderboard-section" className="space-y-6">
                {/* Leaderboard Header */}
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 shadow-xl">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className="bg-gradient-to-br from-[#B8460E]/20 to-orange-600/20 p-4 rounded-xl">
                        <Trophy className="h-8 w-8 text-[#B8460E]" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Leaderboard</h2>
                        <p className="text-gray-400">Top performing affiliates this week</p>
                      </div>
                    </div>
                  </div>

                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 rounded-xl p-4 border border-blue-500/20">
                      <div className="flex items-center gap-3">
                        <Users className="h-8 w-8 text-blue-400" />
                        <div>
                          <p className="text-gray-400 text-sm">Total Affiliates</p>
                          <p className="text-white text-2xl font-bold">127</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-xl p-4 border border-green-500/20">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="h-8 w-8 text-green-400" />
                        <div>
                          <p className="text-gray-400 text-sm">Avg ROAS</p>
                          <p className="text-white text-2xl font-bold">2.57x</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-[#B8460E]/10 to-orange-600/10 rounded-xl p-4 border border-[#B8460E]/20">
                      <div className="flex items-center gap-3">
                        <DollarSign className="h-8 w-8 text-[#B8460E]" />
                        <div>
                          <p className="text-gray-400 text-sm">Total Commissions</p>
                          <p className="text-white text-2xl font-bold">$12.2K</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Leaderboard Table */}
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-white/5 border-b border-white/10">
                          <th className="text-left px-6 py-4 text-sm font-semibold text-gray-300">Rank</th>
                          <th className="text-left px-6 py-4 text-sm font-semibold text-gray-300">Affiliate</th>
                          <th className="text-right px-6 py-4 text-sm font-semibold text-gray-300">Ads</th>
                          <th className="text-right px-6 py-4 text-sm font-semibold text-gray-300">Ad Spend</th>
                          <th className="text-right px-6 py-4 text-sm font-semibold text-gray-300">Avg ROAS</th>
                          <th className="text-right px-6 py-4 text-sm font-semibold text-gray-300">Commission</th>
                          <th className="text-right px-6 py-4 text-sm font-semibold text-gray-300">Trend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mockLeaderboardData.map((affiliate) => (
                          <tr 
                            key={affiliate.rank} 
                            className={`border-b border-white/5 hover:bg-white/5 transition-colors ${
                              affiliate.rank <= 3 ? 'bg-white/5' : ''
                            }`}
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                {getRankIcon(affiliate.rank)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                                  affiliate.rank === 1 ? 'bg-yellow-400/20 text-yellow-400' :
                                  affiliate.rank === 2 ? 'bg-gray-400/20 text-gray-300' :
                                  affiliate.rank === 3 ? 'bg-orange-400/20 text-orange-400' :
                                  'bg-white/10 text-gray-400'
                                }`}>
                                  {affiliate.name.split(' ').map(n => n[0]).join('')}
                                </div>
                                <span className="text-white font-medium">{affiliate.name}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right text-gray-300">{affiliate.adCount}</td>
                            <td className="px-6 py-4 text-right text-green-400 font-semibold">
                              {formatCurrency(affiliate.adSpend)}
                            </td>
                            <td className="px-6 py-4 text-right text-yellow-400 font-semibold">
                              {affiliate.roas.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 text-right text-[#B8460E] font-bold text-lg">
                              {formatCurrency(affiliate.commission)}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <span className="text-green-400 font-semibold">{affiliate.trend}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Your Rank Card */}
                <div className="bg-gradient-to-r from-[#B8460E]/20 via-orange-500/20 to-[#FF6B35]/20 backdrop-blur-xl rounded-2xl border border-[#B8460E]/30 p-6 shadow-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-[#B8460E]/20 p-4 rounded-xl">
                        <Target className="h-10 w-10 text-[#B8460E]" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Your Current Rank</p>
                        <p className="text-white text-3xl font-black">#1</p>
                        <p className="text-[#B8460E] text-sm font-semibold mt-1">🔥 You're leading the pack!</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-400 text-sm">This Week's Earnings</p>
                      <p className="text-white text-3xl font-black">{formatCurrency(summaryStats.totalCommission)}</p>
                      <p className="text-green-400 text-sm font-semibold mt-1">+12% from last week</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
        </div>
      </div>
      {/* End Dashboard Section */}

      {/* Compact Hero Section Below Dashboard - FULL WIDTH, centered, no sidebar offset */}
      <div id="hero-section" className="relative py-16 px-6 overflow-hidden">
        {/* Frosted background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-[600px] h-[600px] bg-[#B8460E]/10 rounded-full blur-[100px] -top-32 -left-32 animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-orange-500/8 rounded-full blur-[80px] -bottom-24 -right-24 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40 backdrop-blur-[1px]"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            {/* Compact badge */}
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-2xl border border-white/20 rounded-full px-4 py-2 mb-6 shadow-xl shadow-[#B8460E]/20 hover:shadow-[#B8460E]/40 transition-all duration-500 group">
              <div className="absolute inset-0 bg-gradient-to-r from-[#B8460E]/20 via-orange-500/20 to-[#FF6B35]/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <Zap className="h-4 w-4 text-[#B8460E] relative z-10 group-hover:scale-110 transition-transform" />
              <span className="font-bold text-xs text-[#B8460E] relative z-10 tracking-wide">THE FIRST EVER IN HISTORY</span>
            </div>
            
            {/* Compact typography */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mb-6 leading-[1.15] tracking-tight px-4">
              <span className="text-white drop-shadow-2xl">We Fund Your Ads.</span>
              <br />
              <span className="relative inline-block mt-2">
                <span className="absolute inset-0 blur-2xl bg-gradient-to-r from-[#B8460E] via-orange-500 to-[#FF6B35] opacity-60"></span>
                <span className="relative bg-gradient-to-r from-[#FF6B35] via-orange-500 to-[#FF6B35] text-transparent bg-clip-text font-black">
                  You Keep The Commission.
                </span>
              </span>
            </h1>
            
            {/* Compact description */}
            <div className="max-w-3xl mx-auto mb-8 px-4">
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 lg:p-6 shadow-2xl">
                <p className="text-base lg:text-lg text-gray-200 leading-relaxed font-medium">
                  Join the world's first affiliate network that funds <span className="text-white font-bold">100% of your ad spend</span>. 
                  Zero risk. Maximum reward. All the tools you need to succeed.
                </p>
              </div>
            </div>

            {/* Compact buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button 
                onClick={() => window.location.href = '/register'}
                className="relative group w-full sm:w-auto overflow-hidden rounded-xl"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#B8460E] via-orange-600 to-[#FF6B35] opacity-100 group-hover:opacity-90 transition-opacity"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B35] via-orange-600 to-[#B8460E] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute inset-[1px] bg-gradient-to-b from-white/10 to-transparent rounded-xl opacity-50"></div>
                <div className="relative px-8 py-3 flex items-center justify-center gap-2">
                  <span className="font-black text-base text-white drop-shadow-lg">Join Olympus Now</span>
                  <ArrowRight className="h-5 w-5 text-white group-hover:translate-x-2 transition-transform duration-300" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
              </button>
              
              <button 
                onClick={() => alert('Learn more about Olympus')}
                className="relative group w-full sm:w-auto overflow-hidden rounded-xl border border-white/20 hover:border-white/40 transition-all duration-300"
              >
                <div className="absolute inset-0 bg-white/5 backdrop-blur-xl group-hover:bg-white/10 transition-colors"></div>
                <div className="relative px-8 py-3">
                  <span className="font-bold text-base text-white">Learn More</span>
                </div>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-[#B8460E]/10 via-orange-500/10 to-[#FF6B35]/10 transition-opacity duration-500 rounded-xl"></div>
              </button>
            </div>
          </div>

          {/* Growth Chart Section */}
          <div className="max-w-6xl mx-auto mb-16">
            <div className="bg-white/5 backdrop-blur-2xl rounded-3xl border border-white/10 p-6 shadow-2xl">
              {/* Header Section - Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                {/* Left: Title and Earnings */}
                <div>
                  <h2 className="text-xl font-bold text-white mb-2">
                    It's Only <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B8460E] to-orange-500">Up</span> From Here
                  </h2>
                  
                  <p className="text-xs text-gray-400 mb-2">Jan 01 - Dec 31, 2024</p>
                  
                  <div className="flex items-center gap-2.5">
                    <div className="text-3xl font-black text-white">$79,900</div>
                    <div className="inline-flex items-center gap-1.5 bg-green-500/20 backdrop-blur-sm border border-green-500/30 text-green-400 px-2.5 py-1 rounded-lg">
                      <TrendingUp className="h-3.5 w-3.5" />
                      <span className="text-xs font-bold">+462%</span>
                    </div>
                  </div>
                </div>

                {/* Right: Description */}
                <div className="flex items-center">
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Our platform is <span className="text-white font-bold">100% free</span>. You can never lose money—only earn it. 
                    It's a <span className="text-[#B8460E] font-bold">true win-win</span> for everyone.
                  </p>
                </div>
              </div>

              {/* Chart */}
              <div className="h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={[
                      { month: 'JAN', value: 2100 },
                      { month: 'FEB', value: 2300 },
                      { month: 'MAR', value: 1900 },
                      { month: 'APR', value: 4800 },
                      { month: 'MAY', value: 5200 },
                      { month: 'JUN', value: 8900 },
                      { month: 'JUL', value: 6200 },
                      { month: 'AUG', value: 7100 },
                      { month: 'SEP', value: 9400 },
                      { month: 'OCT', value: 10200 },
                      { month: 'NOV', value: 11100 },
                      { month: 'DEC', value: 11800 },
                    ]}
                    margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
                  >
                    <defs>
                      <linearGradient id="heroChartGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#B8460E" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#B8460E" stopOpacity={0} />
                      </linearGradient>
                      <filter id="heroChartShadow" x="-50%" y="-50%" width="200%" height="200%">
                        <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.5)" />
                      </filter>
                    </defs>

                    <CartesianGrid
                      strokeDasharray="4 12"
                      stroke="rgba(255,255,255,0.1)"
                      horizontal={true}
                      vertical={false}
                    />

                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: 'rgba(255,255,255,0.6)' }}
                      tickMargin={12}
                    />

                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: 'rgba(255,255,255,0.6)' }}
                      tickFormatter={(value) => `$${value / 1000}K`}
                      domain={[0, 'dataMax + 1000']}
                      tickCount={6}
                      tickMargin={12}
                    />

                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-black/90 backdrop-blur-xl text-white p-3 rounded-lg shadow-xl border border-white/10">
                              <div className="text-xs text-gray-400 mb-1">Total Earnings</div>
                              <div className="text-base font-bold">${payload[0].value.toLocaleString()}</div>
                            </div>
                          );
                        }
                        return null;
                      }}
                      cursor={{
                        stroke: '#B8460E',
                        strokeWidth: 1,
                        strokeDasharray: 'none',
                      }}
                    />

                    {/* Gradient area */}
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="transparent"
                      fill="url(#heroChartGradient)"
                      strokeWidth={0}
                    />

                    {/* Main line */}
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#B8460E"
                      strokeWidth={3}
                      dot={(props) => {
                        const { cx, cy, index } = props;
                        // Highlight June and November
                        if (index === 5 || index === 10) {
                          return (
                            <circle
                              key={`dot-${index}`}
                              cx={cx}
                              cy={cy}
                              r={6}
                              fill="#B8460E"
                              stroke="white"
                              strokeWidth={2}
                              filter="url(#heroChartShadow)"
                            />
                          );
                        }
                        return <g key={`dot-${index}`} />;
                      }}
                      activeDot={{
                        r: 6,
                        fill: '#B8460E',
                        stroke: 'white',
                        strokeWidth: 2,
                        filter: 'url(#heroChartShadow)',
                      }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Compact Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {[
              { icon: DollarSign, title: '100% Funded Ad Spend', desc: 'We cover all your advertising costs. Run campaigns with zero financial risk.', color: 'from-green-500/20 to-emerald-600/20', iconColor: 'text-green-400', badge: 'Zero upfront investment' },
              { icon: BookOpen, title: 'Expert Training', desc: 'Access comprehensive training materials, proven strategies, and insider knowledge.', color: 'from-blue-500/20 to-indigo-600/20', iconColor: 'text-blue-400', badge: 'Learn from the best' },
              { icon: Sparkles, title: 'Premium Content Library', desc: 'Ready-to-use creatives, proven ad copy, and high-converting assets.', color: 'from-purple-500/20 to-pink-600/20', iconColor: 'text-purple-400', badge: 'Thousands of assets' },
              { icon: BarChart3, title: 'Custom Analytics Dashboard', desc: 'Track every metric in real-time with our beautiful, powerful dashboard.', color: 'from-[#B8460E]/20 to-orange-600/20', iconColor: 'text-[#B8460E]', badge: 'Real-time insights' },
              { icon: Upload, title: 'Streamlined Ad Submission', desc: 'Submit and manage your campaigns effortlessly with our intuitive system.', color: 'from-cyan-500/20 to-blue-600/20', iconColor: 'text-cyan-400', badge: 'Quick approval' },
              { icon: Trophy, title: 'Competitive Leaderboards', desc: 'See where you stand. Compete with top affiliates. Earn bonuses.', color: 'from-yellow-500/20 to-orange-600/20', iconColor: 'text-yellow-400', badge: 'Bonus rewards' },
            ].map((feature, idx) => (
              <div key={idx} className="relative group">
                <div className="relative bg-white/5 backdrop-blur-2xl rounded-2xl border border-white/10 p-6 shadow-2xl hover:shadow-[#B8460E]/20 transition-all duration-500 overflow-hidden h-full">
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`}></div>
                  <div className="absolute inset-[1px] bg-gradient-to-b from-white/5 to-transparent rounded-2xl opacity-50"></div>
                  
                  <div className="relative z-10">
                    <div className="relative mb-4 inline-block">
                      <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} blur-lg opacity-50 group-hover:opacity-100 transition-opacity`}></div>
                      <div className={`relative bg-gradient-to-br ${feature.color} backdrop-blur-xl p-3 rounded-xl border border-white/10`}>
                        <feature.icon className={`h-6 w-6 ${feature.iconColor} drop-shadow-lg`} />
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-white transition-colors">{feature.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed mb-3 group-hover:text-gray-300 transition-colors">{feature.desc}</p>
                    
                    <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full px-3 py-1.5 group-hover:bg-white/10 transition-colors">
                      <CheckCircle className="h-3.5 w-3.5 text-green-400" />
                      <span className="text-xs font-semibold text-gray-300">{feature.badge}</span>
                    </div>
                  </div>
                  
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none rounded-2xl"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Hero Stats Cards with Badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-3 shadow-xl relative overflow-hidden group hover:border-[#B8460E]/30 transition-all">
              <div className="absolute inset-0 bg-gradient-to-br from-[#B8460E]/5 to-transparent opacity-50 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative z-10">
                <p className="text-gray-400 text-[10px] font-medium mb-2 uppercase tracking-wide">Ad Spend Funded</p>
                <div className="flex items-center gap-1.5">
                  <p className="text-white text-2xl font-black leading-none">$900K</p>
                  <div className="bg-[#B8460E]/20 backdrop-blur-sm border border-[#B8460E]/30 text-[#B8460E] px-1.5 py-0.5 rounded flex items-center gap-1 shadow-lg flex-shrink-0">
                    <Zap className="h-2.5 w-2.5" />
                    <span className="text-[10px] font-bold">100%</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-3 shadow-xl relative overflow-hidden group hover:border-green-500/30 transition-all">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-50 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative z-10">
                <p className="text-gray-400 text-[10px] font-medium mb-2 uppercase tracking-wide">Active Affiliates</p>
                <div className="flex items-center gap-1.5">
                  <p className="text-white text-2xl font-black leading-none">42</p>
                  <div className="bg-green-500/20 backdrop-blur-sm border border-green-500/30 text-green-400 px-1.5 py-0.5 rounded flex items-center gap-1 shadow-lg flex-shrink-0">
                    <TrendingUp className="h-2.5 w-2.5" />
                    <span className="text-[10px] font-bold">+45%</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-3 shadow-xl relative overflow-hidden group hover:border-[#B8460E]/30 transition-all">
              <div className="absolute inset-0 bg-gradient-to-br from-[#B8460E]/5 to-transparent opacity-50 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative z-10">
                <p className="text-gray-400 text-[10px] font-medium mb-2 uppercase tracking-wide">Paid in Commissions</p>
                <div className="flex items-center gap-1.5">
                  <p className="text-white text-2xl font-black leading-none">$37K</p>
                  <div className="bg-[#B8460E]/20 backdrop-blur-sm border border-[#B8460E]/30 text-[#B8460E] px-1.5 py-0.5 rounded flex items-center gap-1 shadow-lg flex-shrink-0">
                    <DollarSign className="h-2.5 w-2.5" />
                    <span className="text-[10px] font-bold">+28%</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-3 shadow-xl relative overflow-hidden group hover:border-yellow-500/30 transition-all">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-transparent opacity-50 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative z-10">
                <p className="text-gray-400 text-[10px] font-medium mb-2 uppercase tracking-wide">Average ROAS</p>
                <div className="flex items-center gap-1.5">
                  <p className="text-white text-2xl font-black leading-none">1.41x</p>
                  <div className="bg-yellow-500/20 backdrop-blur-sm border border-yellow-500/30 text-yellow-400 px-1.5 py-0.5 rounded flex items-center gap-1 shadow-lg flex-shrink-0">
                    <Award className="h-2.5 w-2.5" />
                    <span className="text-[10px] font-bold">Top 5%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Compact Final CTA */}
          <div className="bg-gradient-to-r from-[#B8460E] via-orange-600 to-[#FF6B35] rounded-2xl p-6 text-center relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-2xl font-black text-white mb-3">Ready to Start Earning Risk-Free?</h2>
              <p className="text-sm text-white/90 mb-5 max-w-xl mx-auto">
                Join hundreds of successful affiliates who are scaling their earnings without spending a dollar on ads.
              </p>
              <button 
                onClick={() => window.location.href = '/register'}
                className="bg-white text-[#B8460E] px-6 py-2.5 rounded-xl font-black text-sm shadow-2xl hover:shadow-white/30 hover:scale-105 transition-all duration-300 inline-flex items-center gap-2 group"
              >
                Get Started Today
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-white/80 mt-3 text-[11px]">No credit card required • Instant approval • Free forever</p>
            </div>
          </div>
        </div>
      </div>
      {/* End Hero Section */}
      </div>
      {/* End Main Content */}

      {/* Feature Preview Overlay - Outside main content to avoid transform issues */}
      {showFeaturePreview && featurePreviews[showFeaturePreview] && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-xl z-50 flex items-center justify-center p-6" onClick={() => setShowFeaturePreview(null)}>
          <div className="bg-white/10 backdrop-blur-2xl rounded-2xl border border-white/20 p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-black text-white mb-2">{featurePreviews[showFeaturePreview].title}</h2>
            <p className="text-sm text-gray-300 mb-4">{featurePreviews[showFeaturePreview].description}</p>
            <div className="flex gap-2">
              <button 
                onClick={() => window.location.href = '/register'}
                className="bg-gradient-to-r from-[#B8460E] to-orange-600 hover:from-[#8B3A0E] hover:to-[#B8460E] text-white px-4 py-2 rounded-lg font-bold text-sm shadow-xl flex items-center gap-2"
              >
                Join to Access
                <ArrowRight className="h-4 w-4" />
              </button>
              <button 
                onClick={() => setShowFeaturePreview(null)}
                className="bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 text-white px-4 py-2 rounded-lg font-bold text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
